import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor — auto-refresh the access token on 401
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Only attempt refresh for 401 errors that are not the refresh call itself
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url.includes('/auth/refresh') &&
      !originalRequest.url.includes('/auth/login')
    ) {
      const storedRefreshToken = localStorage.getItem('refreshToken');

      if (!storedRefreshToken) {
        localStorage.removeItem('token');
        window.location.href = '/login';
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Queue requests that arrive while a refresh is already in flight
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const res = await api.post('/auth/refresh', { refreshToken: storedRefreshToken });
        const { token, refreshToken: newRefreshToken } = res.data.data;

        localStorage.setItem('token', token);
        if (newRefreshToken) localStorage.setItem('refreshToken', newRefreshToken);

        api.defaults.headers.common.Authorization = `Bearer ${token}`;
        originalRequest.headers.Authorization = `Bearer ${token}`;

        processQueue(null, token);
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// API functions
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/updatedetails', data),
  updatePassword: (data) => api.put('/auth/updatepassword', data),
  forgotPassword: (email) => api.post('/auth/forgotpassword', { email }),
  resetPassword: (token, password) => api.put(`/auth/resetpassword/${token}`, { password }),
};

export const userAPI = {
  resetEmail: (oldEmail, newEmail) => api.post('/users/reset-email', { oldEmail, newEmail }),
  resetPassword: (id, newPassword) => api.put(`/users/${id}/resetpassword`, { newPassword }),
};

export const branchAPI = {
  getAll: (params) => api.get('/branches', { params }),
  getOne: (id) => api.get(`/branches/${id}`),
  create: (data) => api.post('/branches', data),
  update: (id, data) => api.put(`/branches/${id}`, data),
  delete: (id) => api.delete(`/branches/${id}`),
  approve: (id) => api.patch(`/branches/${id}/approve`),
  reject: (id, reason) => api.patch(`/branches/${id}/reject`, { reason }),
  toggleBlock: (id, reason) => api.patch(`/branches/${id}/block`, { reason }),
  getStats: () => api.get('/branches/stats'),
  changeAdmin: (id, data) => api.patch(`/branches/${id}/change-admin`, data),
  getAdminHistory: (id) => api.get(`/branches/${id}/admin-history`),
  assignCourse: (id, courseId) => api.post(`/branches/${id}/courses`, { courseId }),
  removeCourse: (id, courseId, reason) => api.delete(`/branches/${id}/courses/${courseId}`, { data: { reason } }),
};

export const courseAPI = {
  getCategories: () => api.get('/courses/categories'),
  createCategory: (data) => api.post('/courses/categories', data),
  updateCategory: (id, data) => api.put(`/courses/categories/${id}`, data),

  getAll: (params) => api.get('/courses', { params }),
  getOne: (id) => api.get(`/courses/${id}`),
  create: (data) => api.post('/courses', data),
  update: (id, data) => api.put(`/courses/${id}`, data),
  delete: (id) => api.delete(`/courses/${id}`),

  getSchemes: (courseId) => api.get(`/courses/${courseId}/schemes`),
  createScheme: (courseId, data) => api.post(`/courses/${courseId}/schemes`, data),

  getTerms: (schemeId) => api.get(`/courses/schemes/${schemeId}/terms`),
  createTerm: (schemeId, data) => api.post(`/courses/schemes/${schemeId}/terms`, data),

  getSubjects: (params) => api.get('/courses/subjects', { params }),
  createSubject: (data) => api.post('/courses/subjects', data),
  updateSubject: (id, data) => api.put(`/courses/subjects/${id}`, data),

  getSections: (courseId) => api.get(`/courses/${courseId}/sections`),
  createSection: (courseId, data) => api.post(`/courses/${courseId}/sections`, data),
};

export const studentAPI = {
  getAll: (params) => api.get('/students', { params }),
  getOne: (id) => api.get(`/students/${id}`),
  create: (data) => api.post('/students', data, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  update: (id, data) => api.put(`/students/${id}`, data, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  editBySuperAdmin: (id, data) => api.patch(`/students/${id}/edit`, data, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  delete: (id) => api.delete(`/students/${id}`),
  approve: (id, remarks) => api.patch(`/students/${id}/approve`, { remarks }),
  reject: (id, reason, remarks) => api.patch(`/students/${id}/reject`, { reason, remarks }),
  getHistory: (id) => api.get(`/students/${id}/history`),
  getStats: (params) => api.get('/students/stats', { params }),
  convertToAlumni: (id, remarks) => api.patch(`/students/${id}/convert-to-alumni`, { remarks }),
  getAllAlumni: (params) => api.get('/students/alumni/list', { params }),
  resetPassword: (id, newPassword) => api.put(`/students/${id}/resetpassword`, { newPassword }),
};

export const examinationAPI = {
  getAll: (params) => api.get('/examinations', { params }),
  getOne: (id) => api.get(`/examinations/${id}`),
  create: (data) => api.post('/examinations', data),
  update: (id, data) => api.put(`/examinations/${id}`, data),
  delete: (id) => api.delete(`/examinations/${id}`),
  getStats: (id) => api.get(`/examinations/${id}/stats`),

  // Student Assignment
  assignStudents: (examId, studentIds) => api.post(`/examinations/${examId}/students`, { studentIds }),
  getExamStudents: (examId, params) => api.get(`/examinations/${examId}/students`, { params }),

  // Student Exam Approval
  approveStudentExam: (id) => api.patch(`/student-exams/${id}/approve`),
  rejectStudentExam: (id, reason) => api.patch(`/student-exams/${id}/reject`, { reason }),
  resubmitStudentExam: (id) => api.patch(`/student-exams/${id}/resubmit`),
  bulkApproveStudentExams: (studentExamIds) => api.post('/student-exams/bulk-approve', { studentExamIds }),

  // Student Exam Lists
  getPendingStudentExams: (params) => api.get('/student-exams/pending', { params }),
  getApprovedStudentExams: (params) => api.get('/student-exams/approved', { params }),
  getRejectedStudentExams: (params) => api.get('/student-exams/rejected', { params }),

  // Hall Ticket
  downloadHallTicket: (studentExamId) => api.get(`/student-exams/${studentExamId}/hall-ticket`, { responseType: 'blob' }),

  // Centerwise Student List for printing (JSON data)
  getCenterwiseStudentList: (examinationId, centerId) => api.get('/student-exams/centerwise-print', {
    params: { examination: examinationId, ...(centerId ? { center: centerId } : {}) }
  }),

  // Centerwise Student List PDF from backend (blob)
  getCenterwisePDF: (examinationId, centerId) => api.get('/student-exams/centerwise-pdf', {
    params: { examination: examinationId, ...(centerId ? { center: centerId } : {}) },
    responseType: 'blob',
  }),
};

export const questionAPI = {
  getAll: (params) => api.get('/questions', { params }),
  getByExamination: (examinationId, params) => api.get(`/questions/examination/${examinationId}`, { params }),
  getOne: (id) => api.get(`/questions/${id}`),
  create: (data) => api.post('/questions', data),
  bulkCreate: (data) => api.post('/questions/bulk', data),
  update: (id, data) => api.put(`/questions/${id}`, data),
  delete: (id) => api.delete(`/questions/${id}`),
  getStats: (examinationId) => api.get(`/questions/examination/${examinationId}/stats`),
};

export const studentExamFlowAPI = {
  // Student exam flow
  getMyExams: () => api.get('/student-exam-flow/my-exams'),
  startExam: (studentExamId, data) => api.post(`/student-exam-flow/start/${studentExamId}`, data),
  getExamQuestions: (examinationId, subjectId) => api.get(`/student-exam-flow/questions/${examinationId}?subjectId=${subjectId}`),
  saveAnswer: (data) => api.post('/student-exam-flow/answer', data),
  submitExam: (submissionId, data) => api.post(`/student-exam-flow/submit/${submissionId}`, data),
  getSubmissionStatus: (submissionId) => api.get(`/student-exam-flow/submission/${submissionId}/status`),
};

export const resultAPI = {
  // Student routes
  getMyResults: () => api.get('/results/my-results'),
  getMyResult: (examinationId) => api.get(`/results/my-result/${examinationId}`),

  // Admin routes
  getExamAttendance: (examinationId, params) => api.get(`/results/examination/${examinationId}/attendance`, { params }),
  generateResult: (studentExamId) => api.post(`/results/generate/${studentExamId}`),
  generateAllResults: (examinationId) => api.post(`/results/generate-all/${examinationId}`),
  publishResult: (resultId) => api.post(`/results/publish/${resultId}`),
  publishAllResults: (examinationId) => api.post(`/results/publish-all/${examinationId}`),
  getExaminationResults: (examinationId, params) => api.get(`/results/examination/${examinationId}`, { params }),
};

export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getEnrollmentTrends: () => api.get('/dashboard/enrollment-trends'),
};

export const franchiseEnquiryAPI = {
  create: (data) => api.post('/franchise-enquiries', data),
  getAll: (params) => api.get('/franchise-enquiries', { params }),
  getOne: (id) => api.get(`/franchise-enquiries/${id}`),
  updateStatus: (id, data) => api.patch(`/franchise-enquiries/${id}/status`, data),
  delete: (id) => api.delete(`/franchise-enquiries/${id}`),
  getStats: () => api.get('/franchise-enquiries/stats'),
};

export const consultationRequestAPI = {
  create: (data) => api.post('/consultation-requests', data),
  getAll: (params) => api.get('/consultation-requests', { params }),
  getOne: (id) => api.get(`/consultation-requests/${id}`),
  updateStatus: (id, data) => api.patch(`/consultation-requests/${id}/status`, data),
  delete: (id) => api.delete(`/consultation-requests/${id}`),
  getStats: () => api.get('/consultation-requests/stats'),
};

export const batchAPI = {
  getAll: () => api.get('/batches'),
  getEnabled: () => api.get('/batches/enabled'),
  create: (data) => api.post('/batches', data),
  toggleStatus: (id) => api.patch(`/batches/${id}/toggle`),
  delete: (id) => api.delete(`/batches/${id}`),
};

export const paymentAPI = {
  create: (data) => api.post('/payments', data),
  getAll: (params) => api.get('/payments', { params }),
  getOne: (id) => api.get(`/payments/${id}`),
  verify: (id, remarks) => api.patch(`/payments/${id}/verify`, { remarks }),
  reject: (id, remarks) => api.patch(`/payments/${id}/reject`, { remarks }),
  getStats: (params) => api.get('/payments/stats', { params }),
  getByStudent: (studentId) => api.get(`/payments/student/${studentId}`),
};

export const studentPaymentAPI = {
  create: (data) => api.post('/student-payments', data),
  getByStudent: (studentId) => api.get(`/student-payments/student/${studentId}`),
  getByBranch: () => api.get('/student-payments/branch'),
};

export default api;
